import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import { MOCK_FAX_COUNTS, MOCK_FAX_MESSAGES } from '@constants/Message/faxMock'
import { logger } from '@utils/safeLogger'
import { toApiError } from '../errors/api-error'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_FAX')
const useMockFaxData = getEnv('REACT_APP_USE_FAX_MOCK_DATA')

export const enableMockFaxData = (): boolean =>
  useMockFaxData.trim().toLowerCase() === 'true'

const headers = {
  'Content-Type': 'application/json',
}

// Types
interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

type GenericResponseData = Record<string, unknown>

type FaxResponseData = { fax?: unknown }
type FaxListResponseData = { fax?: unknown[] }

const mockFaxListResponse = (): ApiResponse<FaxListResponseData> => ({
  statusCode: 200,
  message: 'OK (mock)',
  data: { fax: MOCK_FAX_MESSAGES },
})

const mockFaxCountsResponse = (): ApiResponse<{
  counts?: Record<string, number>
}> => ({
  statusCode: 200,
  message: 'OK (mock)',
  data: { counts: MOCK_FAX_COUNTS },
})

const getHeaders = (dbName?: string): Record<string, string> =>
  dbName ? { ...headers, 'x-db-name': dbName } : headers

// ——— Fax (message) ———
export const createFax = async (
  data: Record<string, unknown>,
): Promise<ApiResponse<FaxResponseData>> => {
  try {
    const response = await axios.post<ApiResponse<FaxResponseData>>(
      `${BUSINESS_LAYER_API}/api/v1/fax`,
      data,
      { headers },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in createFax:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getAllFax = async (
  dbName?: string,
  view?: string,
): Promise<ApiResponse<FaxListResponseData>> => {
  if (enableMockFaxData()) {
    return mockFaxListResponse()
  }

  try {
    const params = view ? { view } : undefined
    const response = await axios.get<ApiResponse<FaxListResponseData>>(
      `${BUSINESS_LAYER_API}/api/v1/fax/all`,
      { headers: getHeaders(dbName), params },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in getAllFax:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

type FaxCountsResponseData = { counts?: Record<string, number> }

export const getFaxCounts = async (
  dbName?: string,
): Promise<ApiResponse<FaxCountsResponseData>> => {
  if (enableMockFaxData()) {
    return mockFaxCountsResponse()
  }

  try {
    const response = await axios.get<ApiResponse<FaxCountsResponseData>>(
      `${BUSINESS_LAYER_API}/api/v1/fax/counts`,
      { headers: getHeaders(dbName) },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in getFaxCounts:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getFaxById = async (
  id: string,
): Promise<ApiResponse<FaxResponseData>> => {
  try {
    const response = await axios.get<ApiResponse<FaxResponseData>>(
      `${BUSINESS_LAYER_API}/api/v1/fax/${id}`,
      { headers },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in getFaxById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updateFaxById = async (
  fax_id: string,
  data: Record<string, unknown>,
): Promise<ApiResponse<FaxResponseData>> => {
  try {
    const response = await axios.put<ApiResponse<FaxResponseData>>(
      `${BUSINESS_LAYER_API}/api/v1/fax`,
      { fax_id, data },
      { headers },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in updateFaxById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const bulkUpdateFaxById = async (
  ids: string[],
  data: Record<string, unknown>,
): Promise<ApiResponse<FaxListResponseData>> => {
  try {
    const response = await axios.put<ApiResponse<FaxListResponseData>>(
      `${BUSINESS_LAYER_API}/api/v1/fax/bulk`,
      { ids, data },
      { headers },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in bulkUpdateFaxById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const deleteFaxById = async (
  id: string,
): Promise<ApiResponse<GenericResponseData>> => {
  try {
    const response = await axios.delete<ApiResponse<GenericResponseData>>(
      `${BUSINESS_LAYER_API}/api/v1/fax/${id}`,
      { headers },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in deleteFaxById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const bulkDeleteFaxById = async (
  ids: string[],
): Promise<ApiResponse<GenericResponseData>> => {
  try {
    const response = await axios.delete<ApiResponse<GenericResponseData>>(
      `${BUSINESS_LAYER_API}/api/v1/fax/bulk`,
      { headers, data: { ids } },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in bulkDeleteFaxById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}
